import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Stack,
  Alert,
  TextField,
  Tooltip,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Tabs,
  Tab,
  Portal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Chip,
  Badge,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import InfoIcon from "@mui/icons-material/Info";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import TuneIcon from "@mui/icons-material/Tune";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import EditFieldDialog from "./EditFieldDialog";
import CreateFieldDialog from "./CreateFieldDialog";
import SmartEditView from "./SmartEditView";
import DecisionOptionCountDialog from "./DecisionOptionCountDialog";
import { buildDefaultDecisionOptions } from "../utils/decisionSectionDefaults";
import {
  GO_TO_DEFAULT_SENTINEL,
  applySectionNavigationDefaults,
  getDefaultNextSectionGoTo,
  getSectionGoToSelectValue,
  getSectionGoToTargets,
  normalizeSectionGoTo,
  remapGoToAttribute,
  usesDefaultNextSectionGoTo,
} from "../utils/sectionGoTo";
import { checkSectionPaths, nodesFromStudioState } from "../utils/sectionPathCheck";

// Add this style to prevent text selection
const noSelectStyle = {
  WebkitTouchCallout: "none",
  WebkitUserSelect: "none",
  KhtmlUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  userSelect: "none",
};

const JsonEditor = ({
  fields,
  sections,
  setSections,
  onFieldAssign,
  onFieldsUpdate,
  onOpenCreateDialog,
  editingField,
  onEditField,
  formDetails,
  createFieldDialogOpen = false,
  onCloseCreateDialog,
  onCreateField,
  createFieldInitialData,
  offerCompare = { fields: [] },
  isPurchaseContract = false,
  getFieldForEdit,
  onSelectField,
  onSectionsChange,
  signatures = {},
  additionalFields = [],
  signerAttributeBindings = [],
  initialerAttributeBindings = [],
  subsequentSigningBindings = [],
  subsequentSigning = {},
  signatureAnnotations = [],
  initialsAnnotations = [],
  onFieldPdfBindingSync = null,
  onDecisionSectionFieldReady = null,
}) => {
  const [viewMode, setViewMode] = useState("list"); // "list" or "smart"
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [propertyPanelTab, setPropertyPanelTab] = useState("details"); // "details" | "edit"
  const [editingSection, setEditingSection] = useState(null); // { id, title }
  const [showRemoveAllConfirm, setShowRemoveAllConfirm] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null); // { id, title, fieldCount }
  const [decisionOptionCountDialogOpen, setDecisionOptionCountDialogOpen] = useState(false);
  /** Result of the last "Check Paths" run; null when the dialog is closed. */
  const [pathCheck, setPathCheck] = useState(null);

  // Initialize active section
  useEffect(() => {
    if (sections.length > 0 && !activeSectionId) {
      setActiveSectionId(sections[0].id);
    }
  }, [sections, activeSectionId]);

  // Sync selection to parent for Field Editor tab
  useEffect(() => {
    if (onSelectField) onSelectField(selectedFieldId);
  }, [selectedFieldId, onSelectField]);

  const linkedAdditionalFieldIds = useMemo(
    () =>
      new Set(
        [
          ...(additionalFields || []).map((a) => a.linkedFieldId),
          ...(subsequentSigningBindings || []).map((a) => a.linkedFieldId),
        ].filter((id) => id != null && id !== ""),
      ),
    [additionalFields, subsequentSigningBindings],
  );

  const handleAssignField = (fieldId) => {
    if (!activeSectionId) return;

    // Find the field and other fields
    const fieldToAssign = fields.find(f => f.id === fieldId);
    if (!fieldToAssign) return;

    // Calculate new order: max order in target section + 1
    const targetSectionFields = fields.filter(
      (f) => f.sectionId === activeSectionId && !f.isOverflow,
    );
    const maxOrder = targetSectionFields.reduce((max, f) => Math.max(max, f.order || 0), 0);

    const updatedField = {
      ...fieldToAssign,
      sectionId: activeSectionId,
      order: maxOrder + 1
    };

    const newFields = fields.map(f => f.id === fieldId ? updatedField : f);
    onFieldsUpdate(newFields);

    // Auto-select the assigned field
    setSelectedFieldId(fieldId);
  };

  const handleAddAllFields = () => {
    if (!activeSectionId || unassignedFields.length === 0) return;

    // Calculate starting order: max order in target section + 1
    const targetSectionFields = fields.filter(
      (f) => f.sectionId === activeSectionId && !f.isOverflow,
    );
    let nextOrder = (targetSectionFields.reduce((max, f) => Math.max(max, f.order || 0), 0)) + 1;

    const updatedFields = fields.map(field => {
      // If field is in unassignedFields, assign it to active section with new order
      if (unassignedFields.some(uf => uf.id === field.id)) {
        const updated = {
          ...field,
          sectionId: activeSectionId,
          order: nextOrder
        };
        nextOrder++; // Increment order for the next field
        return updated;
      }
      return field;
    });

    onFieldsUpdate(updatedFields);
  };

  const getNextSectionId = () => {
    const numericIds = sections
      .map((s) => parseInt(s.id.replace(/^section-/, ""), 10))
      .filter((n) => !Number.isNaN(n));
    const max = numericIds.length ? Math.max(...numericIds) : 0;
    return `section-${max + 1}`;
  };

  const handleAddSection = () => {
    const newSectionId = getNextSectionId();
    const newSectionTitle = `New Section ${sections.length + 1}`;
    const newSection = {
      id: newSectionId,
      title: newSectionTitle,
      attributeName: newSectionTitle.trim(),
      subtitle: "",
      goTo: "",
      goToManual: false,
    };

    const updatedSections = applySectionNavigationDefaults([...sections, newSection]);
    setSections(updatedSections);
    onSectionsChange?.();
    setActiveSectionId(newSectionId);
  };

  const commitAddDecisionSection = (optionCount) => {
    const newSectionId = getNextSectionId();
    const newSectionTitle = `New Decision Section ${sections.length + 1}`;
    const newSection = {
      id: newSectionId,
      title: newSectionTitle,
      attributeName: newSectionTitle.trim(),
      type: "decision",
      subtitle: "",
      description: "",
      goTo: null,
      goToManual: true,
    };

    const defaultRules = {
      notes: "",
      sectionNumber: "",
      sectionName: "",
      sectionDescription: "",
      sectionNotes: "",
    };
    const newField = {
      id: `field-${Date.now()}`,
      sectionId: newSectionId,
      order: 1,
      type: "decision",
      label: "Decision",
      attributeName: newSectionTitle.trim(),
      validation: {},
      rules: defaultRules,
      metadata: {
        options: buildDefaultDecisionOptions(optionCount),
      },
      touchedByAnnotator: true,
    };

    const updatedSections = applySectionNavigationDefaults([...sections, newSection]);
    setSections(updatedSections);
    onFieldsUpdate([...fields, newField]);
    onDecisionSectionFieldReady?.(newField);
    onSectionsChange?.();
    setActiveSectionId(newSectionId);
  };

  const handleAddDecisionSection = () => {
    setDecisionOptionCountDialogOpen(true);
  };

  const handleRemoveSection = (sectionId, deleteFields = false) => {
    const sectionFields = fields.filter(f => f.sectionId === sectionId);
    
    let newFields;
    if (deleteFields) {
      // Permanently delete fields
      newFields = fields.filter(f => f.sectionId !== sectionId);
    } else {
      // Return fields to Available Fields (unassign them)
      newFields = fields.map(f => f.sectionId === sectionId ? { ...f, sectionId: null } : f);
    }
    
    onFieldsUpdate(newFields);

    const remainingSections = sections.filter(
      (section) => section.id !== sectionId
    );
    const updatedSections = applySectionNavigationDefaults(remainingSections);
    setSections(updatedSections);
    onSectionsChange?.();

    // Select the previous section or the first one if available
    if (remainingSections.length > 0) {
      // If we removed the active section, pick another one
      if (activeSectionId === sectionId) {
        setActiveSectionId(remainingSections[0].id);
      }
    } else {
      setActiveSectionId(null);
    }
    
    // Clear the selected field if it was deleted
    if (deleteFields && selectedFieldId && sectionFields.some(f => f.id === selectedFieldId)) {
      setSelectedFieldId(null);
    }
  };

  const handleDeleteSectionClick = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    const sectionFields = fields.filter(f => f.sectionId === sectionId);
    setSectionToDelete({
      id: sectionId,
      title: section?.title || 'Section',
      fieldCount: sectionFields.filter((f) => !f.isOverflow).length,
    });
  };

  const handleEditSection = (section) => {
    setEditingSection({ ...section });
  };

  const handleSaveSectionName = () => {
    if (!editingSection || !editingSection.title.trim()) return;

    let baseAttr = (editingSection.attributeName || editingSection.title || "").trim();
    let attributeName = baseAttr;
    let n = 1;
    while (sections.some((s) => s.id !== editingSection.id && (s.attributeName || "").toLowerCase() === attributeName.toLowerCase())) {
      attributeName = `${baseAttr}${n}`;
      n += 1;
    }

    const oldSection = sections.find((s) => s.id === editingSection.id);
    const oldAttr = (oldSection?.attributeName || "").trim();
    const usesDefault = usesDefaultNextSectionGoTo(editingSection);
    const goToManual = usesDefault ? editingSection.goToManual === true : true;
    const goTo = goToManual
      ? normalizeSectionGoTo(editingSection.goTo)
      : "";

    let updatedSections = sections.map((s) =>
      s.id === editingSection.id
        ? {
            ...s,
            title: editingSection.title,
            attributeName,
            subtitle: editingSection.subtitle,
            description: editingSection.description,
            goTo,
            goToManual,
          }
        : s,
    );

    const remapped = remapGoToAttribute(updatedSections, fields, oldAttr, attributeName);
    updatedSections = remapped.sections;
    if (remapped.fields !== fields) {
      onFieldsUpdate(remapped.fields);
    }

    setSections(applySectionNavigationDefaults(updatedSections));
    onSectionsChange?.();
    setEditingSection(null);
  };

  const sectionGoToTargets = useMemo(() => {
    if (!editingSection) return [];
    return getSectionGoToTargets(sections, editingSection.id);
  }, [sections, editingSection?.id]);

  const defaultNextGoToLabel = useMemo(() => {
    if (!editingSection) return null;
    const attr = getDefaultNextSectionGoTo(sections, editingSection.id);
    if (!attr) return null;
    const next = sections.find(
      (s) => normalizeSectionGoTo(s.attributeName) === attr && s.id !== editingSection.id,
    );
    return next ? `${next.title} (${attr})` : attr;
  }, [sections, editingSection?.id]);

  const handleMoveSection = (index, direction) => {
    const newSections = [...sections];
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= newSections.length) return;

    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];

    const updatedSections = applySectionNavigationDefaults(newSections);
    setSections(updatedSections);
    onSectionsChange?.();
  };

  // Add drag state management for global cursor
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      document.body.classList.remove("grabbing-cursor");
    };
  }, []);

  const handleDragStart = () => {
    document.body.classList.add("grabbing-cursor");
  };

  const handleDragEnd = (result) => {
    document.body.classList.remove("grabbing-cursor");
    const { source, destination, draggableId } = result;

    if (!destination) {
      return;
    }

    // Skip if dropped in the same spot
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    // Validations
    if (!source.droppableId || !destination.droppableId) return;

    // Handle field operations
    const draggedField = fields.find((f) => f.id === draggableId);
    if (!draggedField) return;

    const updatedFields = [...fields];
    const draggedFieldIndex = updatedFields.findIndex((f) => f.id === draggableId);
    if (draggedFieldIndex === -1) return;

    const [removedField] = updatedFields.splice(draggedFieldIndex, 1);

    // Destination is Unassigned
    if (destination.droppableId === "unassigned") {
      removedField.sectionId = null;

      const others = updatedFields.filter((f) => f.sectionId);
      const poolSansDrag = updatedFields.filter((f) => !f.sectionId);
      const overflowPool = poolSansDrag.filter((f) => f.isOverflow);
      const nonOverflowPool = poolSansDrag.filter((f) => !f.isOverflow);
      const visiblePool = nonOverflowPool.filter((f) => !linkedAdditionalFieldIds.has(f.id));
      const hiddenPool = nonOverflowPool.filter((f) => linkedAdditionalFieldIds.has(f.id));

      const nextVisible = [...visiblePool];
      let nextHidden = [...hiddenPool];
      let overflowRest = [...overflowPool];
      if (linkedAdditionalFieldIds.has(removedField.id)) {
        nextHidden = [...hiddenPool, removedField];
      } else if (removedField.isOverflow) {
        overflowRest = [...overflowPool, removedField];
      } else {
        nextVisible.splice(destination.index, 0, removedField);
      }

      const unassigned = [...nextVisible, ...nextHidden, ...overflowRest];
      const newFields = [...unassigned, ...others];
      onFieldsUpdate(newFields);
      setSelectedFieldId(null);
    }
    // Destination is a Section (list UI excludes overflow; merge them back after reordering visible rows)
    else {
      removedField.sectionId = destination.droppableId;
      removedField.touchedByJson = true;

      const destId = destination.droppableId;
      const otherFields = updatedFields.filter((f) => f.sectionId !== destId);
      const inDest = updatedFields
        .filter((f) => f.sectionId === destId)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const overflowInDest = inDest.filter((f) => f.isOverflow);
      const visibleInDest = inDest.filter((f) => !f.isOverflow);

      visibleInDest.splice(destination.index, 0, removedField);
      visibleInDest.forEach((f, idx) => {
        f.order = idx + 1;
      });

      const newFields = [...otherFields, ...visibleInDest, ...overflowInDest];
      onFieldsUpdate(newFields);
      setSelectedFieldId(removedField.id);
    }
  };

  const handleFieldEdit = (field) => {
    onEditField({ ...field });
  };

  const handleSaveField = (updatedField) => {
    const updatedFields = fields.map((field) =>
      field.id === updatedField.id
        ? { ...updatedField, touchedByJson: true }
        : field
    );
    onFieldsUpdate(updatedFields);
    onEditField(null);
  };

  const handleDeleteField = (fieldOrId) => {
    const isObject = typeof fieldOrId === "object" && fieldOrId !== null;
    const fieldId = isObject ? fieldOrId.id : fieldOrId;
    const attributeName = isObject ? fieldOrId.attributeName : null;
    const inlineAttributeName = isObject
      ? fieldOrId.metadata?.content?.[0]?.attributeName
      : null;

    const matchField = (field) => {
      if (fieldId) {
        return String(field.id) === String(fieldId);
      }
      if (attributeName) {
        return field.attributeName === attributeName;
      }
      if (inlineAttributeName) {
        return field.metadata?.content?.[0]?.attributeName === inlineAttributeName;
      }
      if (isObject) {
        const fingerprint = `${field.type}|${field.label}|${field.sectionId}|${field.order}`;
        const targetFingerprint = `${fieldOrId.type}|${fieldOrId.label}|${fieldOrId.sectionId}|${fieldOrId.order}`;
        return fingerprint === targetFingerprint;
      }
      return false;
    };

    const updatedFields = fields.filter((field) => !matchField(field));

    onFieldsUpdate(updatedFields);
    if (fieldId && selectedFieldId === fieldId) setSelectedFieldId(null);
    if (fieldId || attributeName || inlineAttributeName) {
      onEditField(null);
    }
  };

  const unassignedFields = fields.filter(
    (field) =>
      !field.sectionId &&
      !linkedAdditionalFieldIds.has(field.id) &&
      !field.isOverflow,
  );

  const handleRemoveAllFields = () => {
    const updatedFields = fields.filter((field) => {
      if (field.sectionId) return true;
      if (linkedAdditionalFieldIds.has(field.id)) return true;
      if (field.isOverflow) return true;
      return false;
    });
    onFieldsUpdate(updatedFields);
    setShowRemoveAllConfirm(false);
    if (selectedFieldId && unassignedFields.some((f) => f.id === selectedFieldId)) {
      setSelectedFieldId(null);
    }
  };

  // Generic Update Handler
  const handleFieldUpdate = (fieldId, updatedField) => {
    const newFields = fields.map((field) =>
      field.id === fieldId
        ? { ...updatedField, touchedByJson: true }
        : field
    );
    onFieldsUpdate(newFields);
  };

  // Specific Property Handler
  const handlePropertyChange = (field, property, value) => {
    let updatedField = { ...field };

    if (property === "dealSummary" || property === "formSummary") {
      if (value === undefined) {
        const { [property]: _, ...rest } = updatedField;
        updatedField = rest;
      } else {
        updatedField = {
          ...updatedField,
          [property]: value,
        };
      }
    } else {
      updatedField = {
        ...updatedField,
        [property]: value,
      };
    }
    handleFieldUpdate(field.id, updatedField);
  };

  const activeSection = sections.find(s => s.id === activeSectionId);
  const selectedField = fields.find(f => f.id === selectedFieldId);

  return (
    <Box>
      <DecisionOptionCountDialog
        open={decisionOptionCountDialogOpen}
        onClose={() => setDecisionOptionCountDialogOpen(false)}
        onConfirm={commitAddDecisionSection}
        title="New Decision Section"
      />

      {/* goTo path trace: follows each decision option as its own branch */}
      <Dialog open={!!pathCheck} onClose={() => setPathCheck(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Check Paths</DialogTitle>
        <DialogContent dividers>
          {pathCheck && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Traced from <strong>{pathCheck.start || "—"}</strong>. Decision sections branch per
                option; every branch should reach an end of the form and no section should sit off
                every path.
              </Typography>
              {pathCheck.issues.length === 0 ? (
                <Alert severity="success">
                  All {pathCheck.reachable.length} sections are reachable and every path ends at{" "}
                  {pathCheck.ends.join(", ") || "—"}.
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {pathCheck.issues.map((issue, i) => (
                    <Alert key={i} severity={issue.level === "error" ? "error" : "warning"}>
                      {issue.message}
                    </Alert>
                  ))}
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                {pathCheck.reachable.length} reachable · {pathCheck.orphans.length} orphan ·{" "}
                {pathCheck.stranded.length} stranded · ends: {pathCheck.ends.join(", ") || "—"}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPathCheck(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ flexGrow: 1, mr: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>Form Structure</Typography>
          <Typography variant="body2" color="text.secondary">Configure form sections and fields</Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<AltRouteIcon />}
            onClick={() => setPathCheck(checkSectionPaths(nodesFromStudioState(sections, fields)))}
            sx={{ textTransform: 'none' }}
          >
            Check Paths
          </Button>
        </Box>
      </Box>

      {/* Sections List (Wrapping) */}
      <Box sx={{ mb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'transparent',
            p: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {sections.map(section => (
              <Button
                key={section.id}
                onClick={() => setActiveSectionId(section.id)}
                variant={section.id === activeSectionId ? "soft" : "outlined"}
                color={section.id === activeSectionId ? "primary" : "inherit"}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: section.id === activeSectionId ? 'transparent' : 'divider',
                  color: section.id === activeSectionId ? 'primary.main' : 'text.secondary',
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 0.75,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: section.id === activeSectionId ? 'primary.lighter' : 'action.hover',
                    borderColor: section.id === activeSectionId ? 'transparent' : 'text.primary'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {section.title || "Untitled"}
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: section.id === activeSectionId ? 'primary.main' : 'action.selected',
                      color: section.id === activeSectionId ? 'white' : 'text.secondary',
                      px: 0.8,
                      py: 0.2,
                      borderRadius: 1,
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {fields.filter((f) => f.sectionId === section.id && !f.isOverflow).length}
                  </Typography>
                </Box>
              </Button>
            ))}
            
            {/* New Section Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<AddIcon />}
                variant="dashed"
                size="small"
                onClick={handleAddSection}
                sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap', height: '100%' }}
              >
                New Section
              </Button>
              <Button
                startIcon={<AddIcon />}
                variant="dashed"
                size="small"
                onClick={handleAddDecisionSection}
                sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap', height: '100%' }}
              >
                New Decision Section
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Grid container spacing={2}>
          {/* COLUMN 1: Available Fields (Left) */}
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
              <CardHeader
                title="Available Fields"
                titleTypographyProps={{ variant: "subtitle1", fontWeight: "bold" }}
                action={
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 0.5, sm: 1 }, 
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    minWidth: 0,
                    maxWidth: '100%',
                    '& > button': {
                      flexShrink: 0,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 1, sm: 1.5 }
                    }
                  }}>
                    <Button 
                      size="small" 
                      onClick={handleAddAllFields}
                      disabled={!activeSectionId || unassignedFields.length === 0}
                      title="Add all available fields to active section"
                    >
                      Add All
                    </Button>
                    <Button 
                      size="small" 
                      color="error"
                      onClick={() => setShowRemoveAllConfirm(true)}
                      disabled={unassignedFields.length === 0}
                      title="Remove all available fields"
                    >
                      Remove All
                    </Button>
                    <Button size="small" onClick={onOpenCreateDialog}>New</Button>
                  </Box>
                }
                sx={{ 
                  py: 1, 
                  px: { xs: 1, sm: 2 },
                  flexWrap: 'wrap',
                  '& .MuiCardHeader-action': {
                    margin: 0,
                    alignSelf: 'center',
                    width: { xs: '100%', sm: 'auto' },
                    mt: { xs: 1, sm: 0 }
                  },
                  '& .MuiCardHeader-content': {
                    width: { xs: '100%', sm: 'auto' }
                  }
                }}
              />
              <Divider />
              <CardContent sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
                <Droppable droppableId="unassigned" type="field">
                  {(provided, snapshot) => (
                    <List
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{ minHeight: 100, p: 1 }}
                    >
                      {unassignedFields.map((field, index) => (
                        <Draggable key={field.id} draggableId={field.id} index={index}>
                          {(provided, snapshot) => (
                            <ListItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => setSelectedFieldId(field.id)}
                              sx={{
                                ...provided.draggableProps.style,
                                ...noSelectStyle,
                                bgcolor: selectedFieldId === field.id ? "primary.lighter" : (snapshot.isDragging ? 'action.selected' : 'background.paper'),
                                opacity: snapshot.isDragging ? 0.6 : 1,
                                border: '1px solid',
                                borderColor: selectedFieldId === field.id ? "primary.main" : (snapshot.isDragging ? 'primary.main' : 'divider'),
                                borderRadius: 1,
                                mb: 1,
                                cursor: 'pointer',
                                transition: 'all 0.1s',
                                '&:hover': {
                                  bgcolor: selectedFieldId === field.id ? "rgba(0, 168, 150, 0.12)" : 'action.hover',
                                  borderColor: selectedFieldId === field.id ? "primary.main" : 'text.secondary'
                                },
                                ...(selectedFieldId === field.id && {
                                  bgcolor: "rgba(0, 168, 150, 0.08)",
                                })
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", width: "100%", overflow: 'hidden' }}>
                                <Tooltip title="Add to Active Section">
                                  <IconButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAssignField(field.id);
                                    }}
                                    size="small"
                                    sx={{ mr: 1, color: "primary.main", p: 0.5 }}
                                    disabled={!activeSectionId}
                                  >
                                    <AddCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit Field">
                                  <IconButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFieldEdit(field);
                                    }}
                                    size="small"
                                    sx={{ mr: 1, color: "text.secondary", p: 0.5 }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                    {field.label || field.attributeName || "Unnamed"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                                    {field.type}
                                  </Typography>
                                </Box>
                              </Box>
                            </ListItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </CardContent>
            </Card>
          </Grid>

          {/* COLUMN 2: Active Section Structure (Middle) */}
          <Grid item xs={12} md={5}>
            {activeSection ? (
              <Card sx={{ height: '70vh', display: 'flex', flexDirection: 'column', borderTop: "4px solid", borderTopColor: "primary.main" }}>
                <CardHeader
                  title={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', color: 'text.secondary' }}>
                          {activeSection.title}
                        </Typography>
                      </Box>
                      {activeSection.subtitle && (
                        <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                          {activeSection.subtitle}
                        </Typography>
                      )}
                      {activeSection.description && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                          {activeSection.description}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ py: 1.5, px: 2, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}
                  action={
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleMoveSection(sections.findIndex(s => s.id === activeSection.id), -1)} 
                        disabled={sections.findIndex(s => s.id === activeSection.id) === 0}
                        sx={{ mr: 1 }}
                      >
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleMoveSection(sections.findIndex(s => s.id === activeSection.id), 1)} 
                        disabled={sections.findIndex(s => s.id === activeSection.id) === sections.length - 1}
                        sx={{ mr: 2 }}
                      >
                        <ArrowForwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleEditSection(activeSection)} sx={{ mr: 1 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteSectionClick(activeSection.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                />
                <CardContent sx={{ flexGrow: 1, overflowY: 'auto', p: 0, bgcolor: 'background.default' }}>
                  <Droppable droppableId={activeSection.id} type="field">
                    {(provided, snapshot) => (
                      <List
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{ p: 2, minHeight: 200 }}
                      >
                        {fields
                          .filter((f) => f.sectionId === activeSection.id && !f.isOverflow)
                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((field, index) => (
                            <Draggable key={field.id} draggableId={field.id} index={index}>
                              {(provided, snapshot) => (
                                <ListItem
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  onClick={() => setSelectedFieldId(field.id)}
                                  sx={{
                                    ...noSelectStyle,
                                    bgcolor: selectedFieldId === field.id ? "primary.lighter" : (snapshot.isDragging ? "action.selected" : "background.paper"),
                                    opacity: snapshot.isDragging ? 0.6 : 1,
                                    mb: 1,
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor: selectedFieldId === field.id ? "primary.main" : (snapshot.isDragging ? "primary.main" : "divider"),
                                    boxShadow: snapshot.isDragging ? 3 : 0,
                                    cursor: 'pointer',
                                    transition: 'all 0.1s',
                                    "&:hover": { borderColor: "primary.main" },
                                    ...(selectedFieldId === field.id && {
                                      bgcolor: "rgba(0, 168, 150, 0.08)",
                                    })
                                  }}
                                >
                                  {/* Compact Row Content */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', py: 0.5 }}>
                                    <DragIndicatorIcon sx={{ color: 'text.disabled', mr: 1, fontSize: 20 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
                                      {field.label || field.attributeName || "Unnamed"}
                                    </Typography>
                                    <Chip label={field.type} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                  </Box>
                                </ListItem>
                              )}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                        {fields.filter((f) => f.sectionId === activeSection.id && !f.isOverflow).length === 0 && (
                          <Box sx={{ textAlign: 'center', py: 4, opacity: 0.5 }}>
                            <Typography variant="body2">Drag fields here</Typography>
                          </Box>
                        )}
                      </List>
                    )}
                  </Droppable>
                </CardContent>
              </Card>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center', height: '100%', bgcolor: 'transparent' }} elevation={0}>
                <Typography color="text.secondary">Select or create a section</Typography>
              </Paper>
            )}
          </Grid>

          {/* COLUMN 3: Inspector Panel (Right) */}
          <Grid item xs={12} md={4}>
            {createFieldDialogOpen ? (
              <Box sx={{ height: '70vh' }}>
                <CreateFieldDialog
                  open={createFieldDialogOpen}
                  onClose={onCloseCreateDialog}
                  onCreate={onCreateField}
                  sections={sections}
                  fields={fields}
                  mode="json"
                  initialData={createFieldInitialData}
                  variant="panel"
                />
              </Box>
            ) : (
              <Card sx={{ height: '70vh', display: 'flex', flexDirection: 'column', borderLeft: '1px solid', borderColor: 'divider' }}>
                <CardHeader
                  title="Properties"
                  titleTypographyProps={{ variant: "subtitle2", textTransform: 'uppercase', color: 'text.secondary' }}
                  sx={{ py: 1.5, px: 2, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}
                />
                <Tabs
                  value={propertyPanelTab}
                  onChange={(_, value) => setPropertyPanelTab(value)}
                  sx={{ minHeight: 40, borderBottom: 1, borderColor: 'divider', px: 2 }}
                >
                  <Tab label="Details" value="details" />
                  <Tab label="Edit" value="edit" />
                </Tabs>
                <CardContent sx={{ flexGrow: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column' }}>
                  {propertyPanelTab === "details" && (
                    selectedField ? (
                      <Stack spacing={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Field Details</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>{selectedField.label}</Typography>
                            <IconButton size="small" onClick={() => handleFieldEdit(selectedField)}><EditIcon fontSize="small" /></IconButton>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {selectedField.attributeName}
                          </Typography>
                          <Chip label={selectedField.type} size="small" sx={{ mt: 1 }} />
                        </Box>

                        <Divider />

                        {selectedField.type !== "inlineInput" && (
                          <>
                            <Box>
                              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TuneIcon fontSize="small" color="action" /> Settings
                              </Typography>
                              <Stack spacing={2} sx={{ mt: 2 }}>
                                <FormControl size="small" fullWidth>
                                  <Typography variant="caption" gutterBottom>Deal Summary</Typography>
                                  <Select
                                    value={selectedField.dealSummary === undefined ? "exclude" : (selectedField.dealSummary ? "show" : "hide")}
                                    onChange={(e) => handlePropertyChange(selectedField, "dealSummary", e.target.value === "exclude" ? undefined : e.target.value === "show")}
                                    variant="outlined"
                                  >
                                    <MenuItem value="exclude">Do not include</MenuItem>
                                    <MenuItem value="hide">Include (Hidden)</MenuItem>
                                    <MenuItem value="show">Include (Shown)</MenuItem>
                                  </Select>
                                </FormControl>

                                <FormControl size="small" fullWidth>
                                  <Typography variant="caption" gutterBottom>Form Summary</Typography>
                                  <Select
                                    value={selectedField.formSummary === undefined ? "exclude" : (selectedField.formSummary ? "show" : "hide")}
                                    onChange={(e) => handlePropertyChange(selectedField, "formSummary", e.target.value === "exclude" ? undefined : e.target.value === "show")}
                                    variant="outlined"
                                  >
                                    <MenuItem value="exclude">Do not include</MenuItem>
                                    <MenuItem value="hide">Include (Hidden)</MenuItem>
                                    <MenuItem value="show">Include (Shown)</MenuItem>
                                  </Select>
                                </FormControl>
                              </Stack>
                            </Box>

                            <Divider />

                            <Box>
                              <Stack spacing={1}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedField.formUpload || false}
                                      onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        handleFieldUpdate(selectedField.id, {
                                          ...selectedField,
                                          formUpload: isChecked,
                                          metadata: { ...selectedField.metadata, formUploadData: isChecked ? selectedField.attributeName : undefined }
                                        });
                                      }}
                                    />
                                  }
                                  label={<Typography variant="body2">Include in Form Upload</Typography>}
                                />
                                {isPurchaseContract && (
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={offerCompare.fields.some((f) => f.key === selectedField.attributeName)}
                                        onChange={(e) => {
                                          handleFieldUpdate(selectedField.id, {
                                            ...selectedField,
                                            offerCompare: e.target.checked || undefined,
                                          });
                                        }}
                                      />
                                    }
                                    label={<Typography variant="body2">Include in Offer Compare</Typography>}
                                  />
                                )}
                              </Stack>
                            </Box>
                          </>
                        )}

                        <Box sx={{ mt: 'auto !important', pt: 4 }}>
                          <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteField(selectedField.id)}
                          >
                            Remove Field
                          </Button>
                        </Box>
                      </Stack>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5, gap: 2 }}>
                        <TuneIcon sx={{ fontSize: 48 }} color="action" />
                        <Typography variant="body2" align="center">
                          Select a field from the middle column to edit its properties.
                        </Typography>
                      </Box>
                    )
                  )}
                  {propertyPanelTab === "edit" && (
                    selectedField ? (
                      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                        <EditFieldDialog
                          key={(getFieldForEdit ? getFieldForEdit(selectedField) : selectedField)?.id}
                          open={true}
                          field={getFieldForEdit ? getFieldForEdit(selectedField) : selectedField}
                          daysAfterSettings={formDetails?.settings?.daysAfter || {}}
                          sections={sections}
                          fields={fields}
                          signatures={signatures}
                          additionalFields={additionalFields}
                          signerAttributeBindings={signerAttributeBindings}
                          initialerAttributeBindings={initialerAttributeBindings}
                          subsequentSigningBindings={subsequentSigningBindings}
                          subsequentSigning={subsequentSigning}
                          signatureAnnotations={signatureAnnotations}
                          initialsAnnotations={initialsAnnotations}
                          onFieldPdfBindingSync={onFieldPdfBindingSync}
                          onClose={() => setPropertyPanelTab("details")}
                          onSave={(updatedField) => {
                            handleSaveField(updatedField);
                            setPropertyPanelTab("details");
                          }}
                          onDelete={(fieldOrId) => {
                            handleDeleteField(fieldOrId);
                            setPropertyPanelTab("details");
                          }}
                          mode="json"
                          variant="panel"
                          isPurchaseContract={isPurchaseContract}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5, gap: 2 }}>
                        <TuneIcon sx={{ fontSize: 48 }} color="action" />
                        <Typography variant="body2" align="center">
                          Select a field from the middle column to edit its properties.
                        </Typography>
                      </Box>
                    )
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </DragDropContext>

      <EditFieldDialog
        key={editingField?.id}
        open={!!editingField}
        field={editingField}
        daysAfterSettings={formDetails?.settings?.daysAfter || {}}
        sections={sections}
        fields={fields}
        signatures={signatures}
        additionalFields={additionalFields}
        signerAttributeBindings={signerAttributeBindings}
        initialerAttributeBindings={initialerAttributeBindings}
        subsequentSigningBindings={subsequentSigningBindings}
        subsequentSigning={subsequentSigning}
        signatureAnnotations={signatureAnnotations}
        initialsAnnotations={initialsAnnotations}
        onFieldPdfBindingSync={onFieldPdfBindingSync}
        onClose={() => onEditField(null)}
        onSave={handleSaveField}
        onDelete={handleDeleteField}
      />

      {/* Rename Section Dialog */}
      <Dialog open={!!editingSection} onClose={() => setEditingSection(null)}>
        <DialogTitle>Rename Section</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              autoFocus
              margin="dense"
              label="Section Title"
              fullWidth
              variant="outlined"
              value={editingSection?.title || ""}
              onChange={(e) =>
                setEditingSection({ ...editingSection, title: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Attribute Name"
              fullWidth
              variant="outlined"
              value={editingSection?.attributeName || ""}
              onChange={(e) =>
                setEditingSection({ ...editingSection, attributeName: e.target.value })
              }
              error={(() => {
                if (!editingSection) return false;
                const attrName = (editingSection.attributeName || editingSection.title || "").trim();
                if (!attrName) return false;
                const lower = attrName.toLowerCase();
                const isDupField = fields.some((f) => (f.attributeName || "").toLowerCase() === lower);
                const isDupSection = sections.some(
                  (s) => s.id !== editingSection.id && (s.attributeName || "").toLowerCase() === lower,
                );
                return isDupField || isDupSection;
              })()}
              helperText={(() => {
                if (!editingSection) return "Unique identifier for this section";
                const attrName = (editingSection.attributeName || editingSection.title || "").trim();
                if (!attrName) return "Unique identifier for this section";
                const lower = attrName.toLowerCase();
                const isDupField = fields.some((f) => (f.attributeName || "").toLowerCase() === lower);
                const isDupSection = sections.some(
                  (s) => s.id !== editingSection.id && (s.attributeName || "").toLowerCase() === lower,
                );
                if (isDupField || isDupSection) return "Attribute name must be unique";
                return "Unique identifier for this section";
              })()}
            />
            <TextField
              margin="dense"
              label="Subtitle"
              fullWidth
              variant="outlined"
              value={editingSection?.subtitle || ""}
              onChange={(e) =>
                setEditingSection({ ...editingSection, subtitle: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={editingSection?.description || ""}
              onChange={(e) =>
                setEditingSection({ ...editingSection, description: e.target.value })
              }
            />
            <FormControl margin="dense" fullWidth variant="outlined">
              <InputLabel id="rename-section-goto-label">Go To Section</InputLabel>
              <Select
                labelId="rename-section-goto-label"
                label="Go To Section"
                value={
                  editingSection
                    ? getSectionGoToSelectValue(editingSection, sections)
                    : GO_TO_DEFAULT_SENTINEL
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    usesDefaultNextSectionGoTo(editingSection) &&
                    value === GO_TO_DEFAULT_SENTINEL
                  ) {
                    setEditingSection({
                      ...editingSection,
                      goToManual: false,
                      goTo: getDefaultNextSectionGoTo(sections, editingSection.id),
                    });
                  } else {
                    setEditingSection({
                      ...editingSection,
                      goToManual: true,
                      goTo: value,
                    });
                  }
                }}
              >
                {usesDefaultNextSectionGoTo(editingSection) && (
                  <MenuItem value={GO_TO_DEFAULT_SENTINEL}>
                    <em>
                      Next section (default)
                      {defaultNextGoToLabel ? `: ${defaultNextGoToLabel}` : " — end of form"}
                    </em>
                  </MenuItem>
                )}
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {sectionGoToTargets.map((target) => (
                  <MenuItem key={target.value} value={target.value}>
                    {target.label} ({target.value})
                  </MenuItem>
                ))}
                {(() => {
                  if (!editingSection) return null;
                  const current = getSectionGoToSelectValue(editingSection, sections);
                  if (
                    !current ||
                    current === GO_TO_DEFAULT_SENTINEL ||
                    sectionGoToTargets.some((t) => t.value === current)
                  ) {
                    return null;
                  }
                  return (
                    <MenuItem key={`orphan-${current}`} value={current}>
                      {current} (not in form)
                    </MenuItem>
                  );
                })()}
              </Select>
              <FormHelperText>
                {usesDefaultNextSectionGoTo(editingSection)
                  ? "Defaults to the next section; pick another target to override."
                  : "Section to navigate to after this one (target attribute name)"}
              </FormHelperText>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingSection(null)}>Cancel</Button>
          <Button 
            onClick={handleSaveSectionName} 
            variant="contained"
            disabled={(() => {
                if (!editingSection || !editingSection.title.trim()) return true;
                const attrName = (editingSection.attributeName || editingSection.title || "").trim();
                if (!attrName) return false;
                const lower = attrName.toLowerCase();
                const isDupField = fields.some((f) => (f.attributeName || "").toLowerCase() === lower);
                const isDupSection = sections.some(
                  (s) => s.id !== editingSection.id && (s.attributeName || "").toLowerCase() === lower,
                );
                return isDupField || isDupSection;
            })()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove All Confirmation Dialog */}
      <Dialog open={showRemoveAllConfirm} onClose={() => setShowRemoveAllConfirm(false)}>
        <DialogTitle>Remove All Available Fields?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove all {unassignedFields.length} unassigned field{unassignedFields.length !== 1 ? 's' : ''}? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRemoveAllConfirm(false)}>Cancel</Button>
          <Button onClick={handleRemoveAllFields} color="error" variant="contained">
            Remove All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Section Confirmation Dialog */}
      <Dialog open={!!sectionToDelete} onClose={() => setSectionToDelete(null)}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {sectionToDelete?.fieldCount > 0 ? (
              <>
                The section "{sectionToDelete?.title}" contains {sectionToDelete?.fieldCount} field{sectionToDelete?.fieldCount !== 1 ? 's' : ''}. 
                What would you like to do with {sectionToDelete?.fieldCount !== 1 ? 'these fields' : 'this field'}?
              </>
            ) : (
              <>
                Are you sure you want to delete the section "{sectionToDelete?.title}"? This section has no fields.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, alignItems: 'stretch', px: 3, pb: 2 }}>
          {sectionToDelete?.fieldCount > 0 && (
            <>
              <Button 
                onClick={() => {
                  handleRemoveSection(sectionToDelete.id, false);
                  setSectionToDelete(null);
                }} 
                variant="outlined"
                fullWidth
                sx={{ mb: 1 }}
              >
                Return fields to Available Fields
              </Button>
              <Button 
                onClick={() => {
                  handleRemoveSection(sectionToDelete.id, true);
                  setSectionToDelete(null);
                }} 
                color="error" 
                variant="outlined"
                fullWidth
                sx={{ mb: 1 }}
              >
                Permanently delete fields
              </Button>
            </>
          )}
          {sectionToDelete?.fieldCount === 0 && (
            <Button 
              onClick={() => {
                handleRemoveSection(sectionToDelete.id, false);
                setSectionToDelete(null);
              }} 
              color="error" 
              variant="contained"
              fullWidth
              sx={{ mb: 1 }}
            >
              Delete Section
            </Button>
          )}
          <Button 
            onClick={() => setSectionToDelete(null)} 
            variant="text"
            fullWidth
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JsonEditor;
